<template>
  <div>
    <validation-observer
      ref="observer"
      v-slot="{ invalid }"
    >
      <v-form v-model="valid">
        <div>
          <ekitabu-masked-phone-input
            id="buy-now-phone-input"
            class="fill-width mb-3"
            :value="accountPhone"
            :label="$t('checkout.form.phoneNumber')"
            rules="required|max:9|min:9"
            placeholder="### ### ###"
            type="tel"
            required
            :dropdown-disabled="true"
            @formattedPhoneChanged="setFormattedPhone"
          />
          <delivery-options
            @updateShippingInfo="setShippingInfo"
            @updateSchoolInfo="setSchoolInfo"
            @updateIsPickupOrder="setIsPickupOrder"
            @updateIsValid="setDeliveryOptionsValid"
          />
        </div>
        <ApolloMutation
          :mutation="(gql) => placeBuyNowMutation"
          :variables="{
            input: {
              orderId: orderId,
              phoneNumber: formattedPhone,
              locale: locale,
              deliveryOption: deliveryOptionValue,
              alternativePhoneNumber: shippingInfo.altPhoneNumber,
              placeId: shippingInfo.placeId,
              addressDetails: shippingInfo.addressDetails,
              schoolId: schoolInfo.schoolId,
              studentName: schoolInfo.studentName,
              studentClass: schoolInfo.studentClass,
            },
          }"
          @done="onDone"
          @error="onError"
        >
          <template #default="{ mutate, loading: mutationLoading }">
            <PrimaryButton
              id="buy-now-submit"
              class="text-uppercase"
              block
              :disabled="mutationLoading || invalid || !deliveryOptionsValid"
              :loading="mutationLoading"
              @click="mutate()"
            >
              {{ $t("checkout.form.placeOrder") }}
              <EkitabuIcon
                class="ml-2 has-glow"
                x-small
              >
                $triangleRight
              </EkitabuIcon>
            </PrimaryButton>
          </template>
        </ApolloMutation>
      </v-form>
    </validation-observer>
  </div>
</template>

<script>
import { required, max, min } from "vee-validate/dist/rules";
import { extend, ValidationObserver, setInteractionMode } from "vee-validate";
import i18n from "@/i18n";
import EkitabuMaskedPhoneInput from "@/components/EkitabuMaskedPhoneInput";
import DeliveryOptions from "@/views/checkout/print/DeliveryOptions";
import OrderQueries from "@/graphql/OrderQueries";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import gql from "graphql-tag";

setInteractionMode("eager");

extend("required", {
  ...required,
  message: i18n.t("errors.required", { field: "{_field_}" }),
});

extend("max", {
  ...max,
  message: i18n.t("errors.maxLength", {
    field: "{_field_}",
    length: "{length}",
  }),
});

extend("min", {
  ...min,
  message: i18n.t("errors.minLength", {
    field: "{_field_}",
    length: "{length}",
  }),
});

export default {
  name: "BuyNowPaymentForm",
  components: {
    ValidationObserver,
    EkitabuMaskedPhoneInput,
    DeliveryOptions,
    PrimaryButton,
  },
  props: {
    orderId: {
      type: String,
      required: true,
    },
    locale: {
      type: String,
      default: "KEN",
    },
  },
  data() {
    return {
      valid: false,
      formattedPhone: "",
      accountPhone: "",
      deliveryOptionsValid: false,
      shippingInfo: {
        altPhoneNumber: null,
        addressDetails: null,
        placeId: null,
      },
      schoolInfo: {
        schoolId: null,
        studentName: null,
        studentClass: null,
      },
      isPickupOrder: false,
    };
  },
  computed: {
    placeBuyNowMutation() {
      return OrderQueries.placeBuyNow;
    },
    deliveryOptionValue() {
      if (this.isPickupOrder) return "pickUp";
      if (this.schoolInfo.schoolId) return "deliverToSchool";
      return "delivery";
    },
  },
  mounted() {
    if (this.$auth?.isAuthenticated) {
      this.fetchAccountPhone();
    }
  },
  methods: {
    setFormattedPhone(value) {
      this.formattedPhone = value;
    },
    setShippingInfo(shippingInfo) {
      this.shippingInfo = shippingInfo;
    },
    setSchoolInfo(schoolInfo) {
      this.schoolInfo = schoolInfo;
    },
    setIsPickupOrder(isPickupOrder) {
      this.isPickupOrder = isPickupOrder;
    },
    setDeliveryOptionsValid(isValid) {
      this.deliveryOptionsValid = isValid;
    },
    async fetchAccountPhone() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query getUserInfo {
              getWebServiceUser {
                id
                phoneNumber
              }
            }
          `,
        });
        this.accountPhone = data.getWebServiceUser?.phoneNumber || "";
      } catch {
        // Guest user — no account phone available
      }
    },
    onDone(result) {
      this.$emit("done", result);
    },
    onError(error) {
      this.$emit("error", error);
    },
  },
};
</script>
